import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { CurrentUserService } from '../services/auth/current-user.service';

/**
 * Permission Guard
 *
 * Protects routes based on permissions instead of roles.
 * This allows for more flexible access control that can be managed
 * dynamically from the UI without code changes.
 *
 * Usage in routes:
 * canActivate: [permissionGuard('page:view-habibi')]
 *
 * @param requiredPermission - The permission string required to access the route
 *                            Format: 'resource:action' (e.g., 'page:view-habibi')
 */
export const permissionGuard = (requiredPermission: string): CanActivateFn => {
	return (_route, state) => {
		const authService = inject(AuthService);
		const currentUserService = inject(CurrentUserService);
		const router = inject(Router);
		const platformId = inject(PLATFORM_ID);

		// In SSR, allow navigation for page rendering
		if (!isPlatformBrowser(platformId)) {
			return true;
		}

		const token = authService.getToken();
		if (!token) {
			// Back to the requested page after logging in
			return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
		}

		// If no specific permission is required, just verify authentication
		if (!requiredPermission) {
			return true;
		}

		// Check if user has the required permission
		const hasPermission = currentUserService.hasPermission(requiredPermission);

		if (hasPermission) {
			return true;
		}

		// User doesn't have permission, redirect to forbidden page or boost
		console.warn(`Access denied: User lacks permission '${requiredPermission}'`);
		router.navigate(['/boost'], {
			queryParams: {
				error: 'access-denied',
				reason: 'insufficient-permissions',
			},
		});

		return false;
	};
};
